// Shared building blocks for the client-side jsPDF reports, so every report
// (repair report, fault analysis, …) has the same header, section titles and
// table design.

export const PDF_MARGIN_X = 40;

// Usable width on A4 (595pt) once both margins are removed. Every table's
// column widths must add up to this so all tables line up down the page.
export const PDF_CONTENT_W = 515;

/** Centred document title + brand line. Returns the y to carry on drawing at. */
export function drawReportHeader(doc, title) {
  const pageW = doc.internal.pageSize.getWidth();
  let y = 54;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(title, pageW / 2, y, { align: 'center' });
  y += 20;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text('VACWAYlockers', pageW / 2, y, { align: 'center' });
  y += 28;

  doc.setFontSize(10);
  return y;
}

/** Bold heading above a table. Returns the y for the table to start at. */
export function drawSectionTitle(doc, y, text) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(text, PDF_MARGIN_X, y);
  return y + 12;
}

/**
 * Native (vector/text) table renderer for jsPDF: blue header, zebra rows,
 * bordered cells, and automatic page breaks that repeat the header.
 *
 * columns: [{ title, width, align: 'left' | 'right' }]  — widths should sum to PDF_CONTENT_W
 * rows:    [{ cells: [...], bold?: boolean }]
 *
 * Returns the new y position.
 */
export function drawTable(doc, startY, columns, rows, opts = {}) {
  const marginX = PDF_MARGIN_X;
  const pageH = doc.internal.pageSize.getHeight();
  const bottom = pageH - 40;
  const padX = 4;
  const fontSize = opts.fontSize || 9;
  const lineH = opts.lineH || fontSize + 4;
  const totalW = columns.reduce((s, c) => s + c.width, 0);
  let y = startY;

  const drawHeader = () => {
    doc.setFillColor(10, 64, 128);
    doc.rect(marginX, y, totalW, lineH + 6, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);
    let x = marginX;
    columns.forEach((col) => {
      const tx = col.align === 'right' ? x + col.width - padX : x + padX;
      doc.text(col.title, tx, y + fontSize + 3, { align: col.align === 'right' ? 'right' : 'left' });
      x += col.width;
    });
    y += lineH + 6;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  };

  drawHeader();

  rows.forEach((row, ri) => {
    const cellLines = columns.map((col, ci) =>
      doc.splitTextToSize(String(row.cells[ci] == null ? '' : row.cells[ci]), col.width - padX * 2)
    );
    const maxLines = Math.max(1, ...cellLines.map((l) => l.length));
    const rowH = maxLines * lineH + 6;

    if (y + rowH > bottom) {
      doc.addPage();
      y = 40;
      drawHeader();
    }

    if (row.bold) {
      doc.setFillColor(226, 232, 240);
      doc.rect(marginX, y, totalW, rowH, 'F');
    } else if (ri % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(marginX, y, totalW, rowH, 'F');
    }

    doc.setFont('helvetica', row.bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.setDrawColor(200, 208, 216);

    let x = marginX;
    columns.forEach((col, ci) => {
      doc.rect(x, y, col.width, rowH);
      const tx = col.align === 'right' ? x + col.width - padX : x + padX;
      cellLines[ci].forEach((ln, li) => {
        doc.text(ln, tx, y + fontSize + 3 + li * lineH, { align: col.align === 'right' ? 'right' : 'left' });
      });
      x += col.width;
    });

    y += rowH;
  });

  doc.setFont('helvetica', 'normal');
  return y;
}
